import { $t } from '@/utils'
import { bindDevice } from '@/api/user'
import { globalEmit, EVENT_TYPES } from './eventBus'

import { getRouter } from '@/router'
import store from '@/store'

export const getGradeText = () => {
    const userInfo = store.getters['user/userInfo']
    const gradeList = store.getters['user/gradeList']
    let grade = '-'

    if (userInfo?.gradeLevelCode == 'OTHER') {
        grade = userInfo?.gradeLevelOther || $t('grade.other')
    } else {
        grade = gradeList.find(item => item.dictKey === userInfo?.gradeLevelCode)?.dictValue || '-'
    }

    return grade
}

export const USER_TOKEN_CACHE_KEY = 'USER_TOKEN'
const LAST_ACTIVE_KEY = 'USER_LAST_ACTIVE_TIME'
const TOKEN_EXPIRE_MS = 7 * 24 * 60 * 60 * 1000 // 7 days

export const getUserToken = () => {
    if (import.meta.env.SSR) return null
    return localStorage.getItem(USER_TOKEN_CACHE_KEY) || null
}

export const cacheUserToken = (token: string) => {
    if (import.meta.env.SSR) return
    localStorage.setItem(USER_TOKEN_CACHE_KEY, token)
    updateLastActiveTime()
}

export const removeUserToken = () => {
    if (import.meta.env.SSR) return
    localStorage.removeItem(USER_TOKEN_CACHE_KEY)
    removeLastActiveTime()
}

export const updateLastActiveTime = () => {
    if (import.meta.env.SSR) return
    localStorage.setItem(LAST_ACTIVE_KEY, String(Date.now()))
}

export const getLastActiveTime = (): number | null => {
    if (import.meta.env.SSR) return null
    const val = localStorage.getItem(LAST_ACTIVE_KEY)
    return val ? Number(val) : null
}

export const removeLastActiveTime = () => {
    if (import.meta.env.SSR) return
    localStorage.removeItem(LAST_ACTIVE_KEY)
}

export const isTokenExpired = (): boolean => {
    const token = getUserToken()
    if (!token) return true
    const lastActive = getLastActiveTime()
    if (!lastActive) {
        // 兼容旧版本：已登录用户升级后无时间戳，补写当前时间，视为未过期
        updateLastActiveTime()
        return false
    }
    return Date.now() - lastActive > TOKEN_EXPIRE_MS
}

export const redirectByLogin = (redirectType?: string) => {
    // If the current route is an iframe route (e.g. Careers), do not redirect on login to prevent losing user content
    const currentRoute = getRouter().currentRoute.value
    if (currentRoute && (currentRoute.name as string)?.startsWith('iframe-')) {
        console.log('User logged in while on an iframe route. Preserving current page.')
        return
    }

    // 根据 redirectType 确定目标路由名称
    let targetRouteName: string
    switch (redirectType) {
        case 'chat':
            targetRouteName = 'chat'
            break
        case 'study':
            targetRouteName = 'study'
            break
        case 'testprep':
            targetRouteName = 'testprep'
            break
        case 'apexam':
            targetRouteName = 'apexam'
            break
        case 'video':
            targetRouteName = 'video'
            break
        case 'expert':
        case 'expert_help':
            targetRouteName = 'expert'
            break
        case 'expert_answer':
            targetRouteName = 'expert-answer'
            break
        case 'feedback':
            targetRouteName = 'feedback'
            break
        case 'svip':
            targetRouteName = 'svip'
            break
        case 'home':
            targetRouteName = 'user-home'
            break
        default:
            // 默认登录成功后跳转用户首页 /home
            targetRouteName = 'user-home'
            break
    }

    // 检查当前路由是否已经是目标路由
    const currentRouteName = getRouter().currentRoute.value.name
    if (currentRouteName === targetRouteName) {
        // 如果当前路由已经是目标路由，则重载页面
        globalEmit(EVENT_TYPES.RELOAD_ROUTER)
    } else {
        // 否则正常跳转
        getRouter().push({ name: targetRouteName })
    }
}

export const checkUserBase = async () => {
    return new Promise(async resolve => {
        try {
            // 上报设备 匿名登录
            if (!getUserToken()) {
                await bindDevice()
                await store.dispatch('user/initUserInfo')
            } else {
                await store.dispatch('user/initUserInfo')
            }
        } catch (err) {
            console.error('Base info lost', err)
        }

        resolve(true)
    })
}
